import { useRef } from "react";
import { motion, useAnimationFrame, useReducedMotion, useScroll, useTransform } from "framer-motion";

/**
 * Marquee — the quiet transition band that bridges the ink Hero into the
 * Selected Work exhibition. It stays in the ink family so the scroll reads as
 * one continuous cinematic flow.
 *
 * Setup: horizontal two-row layout (opposing directions). Kept: the slower
 * cadence and the glowing gold "light" dots. Added: a subtle scroll-linked
 * PARALLAX so the two rows drift a little as the band passes through the
 * viewport, making it feel alive — no crossing.
 *
 * Motion model (rAF-driven for buttery speed control)
 * ---------------------------------------------------
 * Each track is advanced every frame by a velocity that eases between FAST and
 * SLOW (pointer/touch slows it for readability). That auto-scroll lives on the
 * inner track's transform; the parallax lives on a separate wrapper so the two
 * never fight. Reduced-motion freezes both.
 */
const WORDS = [
    "Brand Identity",
    "Logo Systems",
    "Creative Direction",
    "Visual Strategy",
    "Premium Visual Design",
    "Motion",
    "UI / UX Direction",
];

const REPEAT = 3;                 // WORDS rendered 3x per track
const SPAN = 100 / REPEAT;        // % of track width = one WORDS sequence
const DUR_FAST = 16;              // seconds per sequence (the liked, slower pace)
const DUR_SLOW = 68;              // seconds per sequence while hovered/touched
const SPEED_FAST = SPAN / DUR_FAST;   // % of track width per second
const SPEED_SLOW = SPAN / DUR_SLOW;

const Row = ({ reverse, hoverRef, reduce, parallax }) => {
    const trackRef = useRef(null);
    const posRef = useRef(reverse ? -SPAN : 0);
    const speedRef = useRef(SPEED_FAST);
    const items = reverse ? [...WORDS].reverse() : WORDS;
    const cells = Array.from({ length: REPEAT }).flatMap(() => items);

    useAnimationFrame((_t, delta) => {
        if (reduce || !trackRef.current) return;
        const dt = Math.min(delta, 64) / 1000;
        const target = hoverRef.current ? SPEED_SLOW : SPEED_FAST;
        // Ease velocity so speed changes feel smooth, never snapped.
        speedRef.current += (target - speedRef.current) * 0.06;
        let p = posRef.current + (reverse ? 1 : -1) * speedRef.current * dt;
        if (!reverse && p <= -SPAN) p += SPAN;
        if (reverse && p >= 0) p -= SPAN;
        posRef.current = p;
        trackRef.current.style.transform = `translate3d(${p}%,0,0)`;
    });

    return (
        <motion.div className="pg-transition__row" style={reduce ? undefined : { x: parallax }}>
            <div
                className="pg-transition__track"
                ref={trackRef}
                style={reduce ? { transform: `translate3d(${reverse ? -SPAN / 2 : 0}%,0,0)` } : undefined}
            >
                {cells.map((w, i) => (
                    <span key={`${reverse ? "r" : "f"}-${w}-${i}`} className="pg-transition__cell">
                        <span className="pg-transition__word">{w}</span>
                        <span className="pg-transition__sep" aria-hidden="true" />
                    </span>
                ))}
            </div>
        </motion.div>
    );
};

export const Marquee = () => {
    const reduce = useReducedMotion();
    const hoverRef = useRef(false);
    const sectionRef = useRef(null);

    const setHover = (v) => () => { hoverRef.current = v; };

    // Scroll-linked parallax: the two rows drift a touch in opposite
    // directions as the band travels through the viewport.
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });
    const xFwd = useTransform(scrollYProgress, [0, 1], [-28, 28]);
    const xRev = useTransform(scrollYProgress, [0, 1], [28, -28]);

    return (
        <section
            ref={sectionRef}
            aria-hidden
            className="pg-transition pg-transition--stacked pg-surface-ink"
            data-testid="marquee-strip"
            onPointerEnter={setHover(true)}
            onPointerLeave={setHover(false)}
            onTouchStart={setHover(true)}
            onTouchEnd={setHover(false)}
            onTouchCancel={setHover(false)}
        >
            <Row reverse={false} hoverRef={hoverRef} reduce={reduce} parallax={xFwd} />
            <Row reverse hoverRef={hoverRef} reduce={reduce} parallax={xRev} />
        </section>
    );
};

export default Marquee;
